/**
 * Sanitize a post-login `?redirect=` destination.
 *
 * The `redirect` param is attacker-controllable — it is just a query string on a public
 * `/login` URL — and both readers (`LoginPage`, `AuthCallbackPage`) previously fed it
 * straight into `navigate()`.
 *
 * `?redirect=https://evil.com` is harmless on its own: react-router treats a string `to`
 * as a path, so it resolves to the pathname `/https://evil.com` and stays on-site. The
 * dangerous shape is **protocol-relative** — `?redirect=//evil.com` becomes
 * `history.push('//evil.com')`, which the browser resolves against the current scheme and
 * navigates off-site. `\` is folded to `/` by browsers, so `\\evil.com` and `/\evil.com`
 * are the same attack; backslashes are rejected outright rather than normalized.
 *
 * Policy: allow only same-origin absolute paths — a single leading `/`, no scheme, no
 * authority. Anything else falls back to the caller's default. Deliberately a whitelist:
 * a blacklist of known-bad prefixes is exactly how these holes get reopened.
 */

/** Paths that must never be a post-login destination — they'd bounce or loop. */
const DISALLOWED_PREFIXES = ['/login', '/signup', '/auth/callback'];

export function safeRedirectPath(raw: string | null | undefined, fallback = '/dashboard'): string {
  if (!raw) return fallback;

  // Reject control characters: browsers and URL parsers strip \t, \n and \r, so
  // "/\t/evil.com" can normalize into something that slips past the checks below.
  // A literal space is deliberately allowed — it is legitimate inside a query value
  // and cannot change the origin.
  // eslint-disable-next-line no-control-regex -- matching control characters is the point
  if (/[\x00-\x1F\x7F]/.test(raw)) return fallback;

  // Backslashes are treated as forward slashes by browsers, so "/\evil.com" is
  // protocol-relative in disguise.
  if (raw.includes('\\')) return fallback;

  // Must be an absolute same-origin path: exactly one leading slash.
  if (!raw.startsWith('/')) return fallback;
  if (raw.startsWith('//')) return fallback;

  // A scheme can only appear here if the value is not really a path.
  if (/^\/[a-z][a-z0-9+.-]*:/i.test(raw)) return fallback;

  // Never send the user back to an auth screen — at best a no-op, at worst a loop.
  const pathOnly = raw.split(/[?#]/)[0].toLowerCase();
  if (DISALLOWED_PREFIXES.some((p) => pathOnly === p || pathOnly.startsWith(`${p}/`))) {
    return fallback;
  }

  return raw;
}

/**
 * Build the `/login?redirect=…` URL a guard should bounce an unauthenticated user to.
 * Returns a bare `/login` when the destination is not worth preserving, so we never emit
 * a pointless `?redirect=/dashboard`.
 */
export function loginUrlWithRedirect(target: string, fallback = '/dashboard'): string {
  const safe = safeRedirectPath(target, fallback);
  if (safe === fallback) return '/login';
  return `/login?redirect=${encodeURIComponent(safe)}`;
}
